/**
 * Emergent Marketplace — Phase 4
 * BookingConfirmation.jsx
 * Architectural Layer: Presentation → Customer
 *
 * Shown after a customer successfully completes an in-app booking (Mode 1).
 * Displays booking summary, next steps, add-to-calendar options.
 * Also handles the "booking-complete" return URL for Mode 3 external bookings.
 *
 * Design: Ceremony of completion — deep forest, soft gold, reverent space.
 * Font: Cormorant Garamond (display) + DM Sans (body)
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Check, Calendar, Clock, Video, User, Mail,
  ChevronRight, Download, Bell, Share2, ArrowLeft
} from "lucide-react";
import { apiFetch } from "./api";
import IntakeFormsBlock from "@/components/marketplace/IntakeFormsBlock";
import {
  getGoogleCalendarUrl,
  getAppleCalendarDataUri,
  downloadICS,
} from "@/utils/calendarUtils";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  .bc-root {
    --forest:      #1b3a2d;
    --forest-mid:  #2d5a44;
    --forest-light:#4a8a6a;
    --gold:        #c8a040;
    --gold-light:  #e0c060;
    --gold-bg:     #fdf8e8;
    --cream:       #f8f5ee;
    --cream-dark:  #ede8d8;
    --white:       #ffffff;
    --border:      #ddd8c8;
    --ink:         #1a1a14;
    --ink-mid:     #5a5a4a;
    --ink-light:   #9a9a88;
    font-family: 'DM Sans', sans-serif;
    background: var(--cream);
    color: var(--ink);
    min-height: 100vh;
  }

  .bc-container {
    max-width: 480px;
    margin: 0 auto;
    padding: 0 0 60px;
  }

  /* ── Success header ────────────────────────────────────────────────── */
  .bc-hero {
    background: linear-gradient(160deg, var(--forest) 0%, var(--forest-mid) 100%);
    padding: 48px 32px 36px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .bc-hero::before {
    content: '';
    position: absolute;
    top: -60px; left: 50%;
    transform: translateX(-50%);
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(200,160,64,0.12) 0%, transparent 70%);
    pointer-events: none;
  }

  .bc-check-circle {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: rgba(200,160,64,0.15);
    border: 2px solid rgba(200,160,64,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    position: relative;
    z-index: 1;
    animation: bc-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes bc-pop {
    from { transform: scale(0.6); opacity: 0; }
    to   { transform: scale(1);   opacity: 1; }
  }

  .bc-hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 34px;
    font-weight: 300;
    color: var(--white);
    margin: 0 0 8px;
    position: relative;
    z-index: 1;
  }

  .bc-hero-sub {
    font-size: 14px;
    color: rgba(255,255,255,0.65);
    margin: 0;
    line-height: 1.6;
    position: relative;
    z-index: 1;
  }

  /* ── Booking summary card ──────────────────────────────────────────── */
  .bc-summary {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 16px;
    overflow: hidden;
    margin: 20px 24px;
  }

  .bc-summary-header {
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--cream-dark);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .bc-practitioner-avatar {
    width: 44px; height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--forest), var(--forest-mid));
    color: white;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px; font-weight: 600;
    flex-shrink: 0;
  }

  .bc-practitioner-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 2px;
  }

  .bc-practitioner-session {
    font-size: 12px;
    color: var(--ink-light);
  }

  .bc-summary-rows { padding: 4px 0; }

  .bc-summary-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    border-bottom: 1px solid var(--cream-dark);
    font-size: 14px;
    color: var(--ink-mid);
  }

  .bc-summary-row:last-child { border-bottom: none; }

  .bc-summary-row-icon {
    width: 30px; height: 30px;
    border-radius: 8px;
    background: var(--cream);
    border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: var(--ink-light);
  }

  .bc-summary-label { color: var(--ink-light); font-size: 12px; }
  .bc-summary-value { color: var(--ink); font-weight: 600; margin-top: 1px; }

  .bc-ref-badge {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    padding: 3px 10px;
    border-radius: 12px;
    background: var(--gold-bg);
    color: var(--gold);
    border: 1px solid rgba(200,160,64,0.3);
  }

  /* ── Status block ──────────────────────────────────────────────────── */
  .bc-status-block {
    margin: 0 24px 16px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid;
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .bc-status-block.pending {
    background: var(--gold-bg);
    border-color: rgba(200,160,64,0.3);
  }

  .bc-status-block.confirmed {
    background: #eaf5f0;
    border-color: #a0d8c4;
  }

  .bc-status-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--ink);
    margin: 0 0 4px;
  }

  .bc-status-body {
    font-size: 13px;
    color: var(--ink-mid);
    line-height: 1.55;
    margin: 0;
  }

  /* ── Calendar add ──────────────────────────────────────────────────── */
  .bc-section { padding: 0 24px 16px; }

  .bc-section-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin-bottom: 10px;
  }

  .bc-cal-buttons { display: flex; gap: 8px; flex-wrap: wrap; }

  .bc-cal-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 16px;
    border-radius: 9px;
    border: 1px solid var(--border);
    background: var(--white);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-mid);
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }

  .bc-cal-btn:hover { border-color: var(--forest-light); color: var(--forest); }

  /* ── Prep tips ─────────────────────────────────────────────────────── */
  .bc-tips {
    margin: 0 24px 16px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 16px 18px;
  }

  .bc-tips-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 17px;
    font-weight: 600;
    color: var(--forest);
    margin: 0 0 12px;
  }

  .bc-tip {
    display: flex;
    gap: 10px;
    font-size: 13px;
    color: var(--ink-mid);
    padding: 6px 0;
    line-height: 1.5;
    border-bottom: 1px solid var(--cream-dark);
  }

  .bc-tip:last-child { border-bottom: none; }

  .bc-tip-bullet {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--gold);
    flex-shrink: 0;
    margin-top: 7px;
  }

  /* ── Actions ───────────────────────────────────────────────────────── */
  .bc-actions { padding: 0 24px; display: flex; flex-direction: column; gap: 10px; }

  .bc-btn-primary {
    width: 100%;
    padding: 14px;
    border-radius: 12px;
    background: var(--forest);
    border: none;
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: background 0.15s;
  }

  .bc-btn-primary:hover { background: var(--forest-mid); }

  .bc-btn-secondary {
    width: 100%;
    padding: 12px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: none;
    color: var(--ink-mid);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: border-color 0.15s;
  }

  .bc-btn-secondary:hover { border-color: var(--ink-mid); }

  /* Mode 3 return variant */
  .bc-external-return {
    margin: 20px 24px;
    background: linear-gradient(135deg, var(--forest), var(--forest-mid));
    border-radius: 16px;
    padding: 24px;
    text-align: center;
    color: white;
  }

  .bc-external-return h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 24px;
    font-weight: 400;
    font-style: italic;
    margin: 0 0 8px;
  }

  .bc-external-return p {
    font-size: 14px;
    color: rgba(255,255,255,0.75);
    margin: 0 0 20px;
    line-height: 1.6;
  }

  .bc-return-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 24px;
    border-radius: 10px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .bc-return-btn:hover { background: rgba(255,255,255,0.25); }
`;

// ─── Mock booking data ────────────────────────────────────────────────────────

const MOCK_BOOKING = {
  id:               "bk1",
  ref:              "BK-A8F3K2",
  practitioner_name: "Maya Chen",
  session_title:    "Clarity Session",
  duration_minutes: 60,
  price:            120,
  scheduled_at:     "2026-03-18T15:00:00",
  timezone:         "America/New_York",
  format:           "Video call",
  status:           "requested",   // "requested" | "confirmed"
  source:           "in_app",      // "in_app" | "widget" | "external"
  confirmation_message: null,
};

// ─── Calendar helpers ─────────────────────────────────────────────────────────

const toCalendarEvent = (booking) => ({
  title: `${booking.session_title} with ${booking.practitioner_name}`,
  description: `Session booked through 11:11 Collective Consciousness.${booking.notes ? `\n\nNotes: ${booking.notes}` : ''}`,
  // scheduled_at is a true UTC instant, so the export is pinned to the right
  // moment no matter which timezone the person adding it is in
  start_utc: booking.scheduled_at,
  timezone: booking.timezone,
  duration_minutes: booking.duration_minutes || 60,
  // `meeting_link` never existed on a booking anywhere in the backend, so every calendar
  // export has gone out with no link in it. Bookings now carry a resolved `location`.
  link: booking.location?.link || booking.meeting_link || null,
  location: booking.location?.address || booking.location?.link || booking.location?.label || "",
});


const formatDateTime = (iso, tz) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz,
      weekday: "long", month: "long", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch {
    return iso;
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BookingConfirmation({ isExternal = false }) {
  const { bookingId } = useParams();
  const routerNavigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [calAdded, setCalAdded] = useState(false);

  useEffect(() => {
    if (bookingId) {
      apiFetch(`/bookings?per_page=100`).then(res => {
        const list = res?.bookings || res || [];
        const found = list.find(b => b.id === bookingId);
        if (found) setBooking(found);
      }).catch(() => {});
    }
  }, [bookingId]);

  // A client returning from Calendly may never have logged in, so "back to the app" cannot
  // assume a dashboard they are allowed to see.
  const onBack = () => routerNavigate(
    localStorage.getItem("token") ? "/dashboard/marketplace/bookings" : "/"
  );

  // Mode 3 return page — just show a return-to-app card
  if (isExternal) {
    return (
      <>
        <style>{styles}</style>
        <div className="bc-root">
          <div className="bc-container">
            <div className="bc-check-circle" style={{ margin: "40px auto 0" }}>
              <Check size={36} color="#c8a040" strokeWidth={2.5} />
            </div>
            <div className="bc-external-return">
              <h2>Booking complete</h2>
              <p>
                Your session has been booked through your practitioner's scheduling platform.
                Check your email for a confirmation.
              </p>
              <button className="bc-return-btn" onClick={onBack || (() => window.history.back())}>
                <ArrowLeft size={15} /> Back to 11/11 Collective Connection
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!booking) return (
    <div style={{ padding: 40, textAlign: "center", color: "#8b80a8" }}>Loading booking...</div>
  );

  const isPending   = booking.status === "requested";
  const isConfirmed = booking.status === "confirmed";

  return (
    <>
      <style>{styles}</style>
      <div className="bc-root">
        <div className="bc-container">

          {/* Hero */}
          <div className="bc-hero">
            <div className="bc-check-circle">
              <Check size={36} color="#c8a040" strokeWidth={2.5} />
            </div>
            <h1 className="bc-hero-title">
              {isPending ? "Request sent" : "Session confirmed"}
            </h1>
            <p className="bc-hero-sub">
              {isPending
                ? `${booking.practitioner_name} will confirm your booking shortly.`
                : `Your session with ${booking.practitioner_name} is confirmed.`
              }
            </p>
          </div>

          {/* Status block */}
          <div className={`bc-status-block ${isPending ? "pending" : "confirmed"}`}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: isPending ? "rgba(200,160,64,0.12)" : "rgba(26,90,68,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              color: isPending ? "#c8a040" : "#1a5a44",
            }}>
              {isPending ? <Bell size={17} /> : <Check size={17} />}
            </div>
            <div>
              <p className="bc-status-title">
                {isPending ? "Awaiting confirmation" : "Confirmed"}
              </p>
              <p className="bc-status-body">
                {isPending
                  ? "You'll receive a notification when your practitioner accepts. Payment is held and will only be charged upon confirmation."
                  : booking.confirmation_message || "Your practitioner has confirmed your session. You'll receive a reminder before it starts."
                }
              </p>
            </div>
          </div>

          {/* Forms to complete, on the page and not only in the email */}
          <IntakeFormsBlock forms={booking.intake_forms} testId="booking-confirmation-intake" />

          {/* Booking summary */}
          <div className="bc-summary">
            <div className="bc-summary-header">
              <div className="bc-practitioner-avatar">{booking.practitioner_name[0]}</div>
              <div>
                <p className="bc-practitioner-name">{booking.practitioner_name}</p>
                <p className="bc-practitioner-session">{booking.session_title}</p>
              </div>
              <span className="bc-ref-badge" style={{ marginLeft: "auto" }}>{booking.ref}</span>
            </div>
            <div className="bc-summary-rows">
              <div className="bc-summary-row">
                <div className="bc-summary-row-icon"><Calendar size={14} /></div>
                <div>
                  <div className="bc-summary-label">Date & time</div>
                  <div className="bc-summary-value">{formatDateTime(booking.scheduled_at, booking.timezone)}</div>
                </div>
              </div>
              <div className="bc-summary-row">
                <div className="bc-summary-row-icon"><Clock size={14} /></div>
                <div>
                  <div className="bc-summary-label">Duration</div>
                  <div className="bc-summary-value">{booking.duration_minutes} minutes</div>
                </div>
              </div>
              {booking.format && (
                <div className="bc-summary-row">
                  <div className="bc-summary-row-icon"><Video size={14} /></div>
                  <div>
                    <div className="bc-summary-label">Format</div>
                    <div className="bc-summary-value">{booking.format}</div>
                  </div>
                </div>
              )}
              <div className="bc-summary-row">
                <div className="bc-summary-row-icon">
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-light)" }}>$</span>
                </div>
                <div>
                  <div className="bc-summary-label">
                    {isPending ? "Amount (held until confirmed)" : "Amount charged"}
                  </div>
                  <div className="bc-summary-value">${booking.price}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Add to calendar */}
          <div className="bc-section">
            <p className="bc-section-title">Add to calendar</p>
            <div className="bc-cal-buttons">
              <a
                href={getGoogleCalendarUrl(toCalendarEvent(booking))}
                target="_blank"
                rel="noreferrer"
                className="bc-cal-btn"
                onClick={() => setCalAdded(true)}
                data-testid="booking-gcal-btn"
              >
                <Calendar size={14} />
                Google Calendar
              </a>
              <a
                href={getAppleCalendarDataUri(toCalendarEvent(booking))}
                download={`session-${booking.id}.ics`}
                className="bc-cal-btn"
                onClick={() => setCalAdded(true)}
                data-testid="booking-apple-btn"
              >
                <Calendar size={14} />
                Apple Calendar
              </a>
              <button
                className="bc-cal-btn"
                onClick={() => { downloadICS(toCalendarEvent(booking)); setCalAdded(true); }}
                data-testid="booking-ics-btn"
              >
                <Download size={14} />
                Download .ics
              </button>
            </div>
            {calAdded && (
              <p style={{ fontSize: 12, color: "#1a5a44", marginTop: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <Check size={12} /> Added to your calendar
              </p>
            )}
          </div>

          {/* Prep tips */}
          <div className="bc-tips">
            <p className="bc-tips-title">Preparing for your session</p>
            {[
              "Find a quiet, comfortable space with a stable internet connection.",
              "Have a pen and paper or notes app ready for any insights.",
              "Set an intention — what do you most want clarity or support on?",
              "The video call link will be shared once your practitioner confirms.",
            ].map((tip, i) => (
              <div key={i} className="bc-tip">
                <div className="bc-tip-bullet" />
                <span>{tip}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="bc-actions">
            <button className="bc-btn-primary" onClick={() => onBack?.("bookings")}>
              <Calendar size={15} /> View my bookings <ChevronRight size={15} />
            </button>
            <button className="bc-btn-secondary" onClick={() => onBack?.("marketplace")}>
              Back to Marketplace
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
